import React, { type ComponentProps } from 'react'

import type { Icon16 } from './FoundationIcon/icons'
import { IconButton } from './IconButton'
import { ListBox, type Props as ListBoxProps } from './internal/ListBox'
import type { MenuOptionVariantSelectable } from './MenuOption'

type Props<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = Omit<ListBoxProps<TOption>, 'children' | 'menuWidth' | 'placeholder'> & {
  toggleButtonIcon?: Icon16
  toggleButtonForegroundColor?: ComponentProps<
    typeof IconButton
  >['foregroundColor']
  toggleButtonBackgroundColor?: ComponentProps<
    typeof IconButton
  >['backgroundColor']
}

/**
 * The `OverflowMenu` is a dropdown that allows the user to select from a list of options
 *   with a compact footprint when collapsed, not displaying any labels/placeholders nor
 *   currently selected option.
 */
export function OverflowMenu<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
>({
  toggleButtonBackgroundColor,
  toggleButtonForegroundColor,
  toggleButtonIcon,
  ...listBoxProps
}: Props<TOption>) {
  return (
    <ListBox<TOption> {...listBoxProps} menuWidth={200}>
      {({ ref, ...toggleProps }, isMenuOpen) => (
        <IconButton
          {...toggleProps}
          backgroundColor={toggleButtonBackgroundColor}
          foregroundColor={toggleButtonForegroundColor}
          icon={toggleButtonIcon || 'Dots16'}
          innerRef={ref}
          state={
            listBoxProps.state !== 'error' && isMenuOpen
              ? 'active'
              : listBoxProps.state === 'disabled' && 'disabled'
          }
        />
      )}
    </ListBox>
  )
}
